/**
 * Configuração e GATES do OpenReplay.
 *
 * Funções puras, sem Angular e sem o SDK: é o que permite testar cada gate
 * isoladamente e é o que mantém este arquivo no bundle EAGER sem custo — o
 * tracker (~54 KB gz) só entra pelo chunk lazy, e só depois de todo gate aqui
 * dizer sim.
 *
 * ## Por que são DUAS decisões e não uma
 *
 * O `decidirAgendamento` roda no `APP_INITIALIZER`, quando a loja ainda não
 * existe — o cardápio é resolvido por outro initializer, que bloqueia o
 * bootstrap (`core.module.ts`). O `decidirCarga` roda no `load`, já com a loja
 * na mão, e é o único que aplica o gate de loja.
 *
 * Separar não é preciosismo: se o gate de loja rodasse no agendamento, ele
 * reprovaria TODO MUNDO (loja sempre `null` naquele instante) e o tracker nunca
 * carregaria — falha silenciosa, do tipo que só aparece quando alguém pergunta
 * "cadê as sessões". E se o gate de loja não existisse até a flag de backend
 * chegar, ligá-la depois exigiria mexer no serviço, no runtime e nos specs.
 */

import { inSample } from '../trace-utils';
import { ehLocalhost } from '../gate-utils';

/** Bloco `openReplay` dos environments. */
export interface OpenReplayConfig {
  /**
   * Chave do projeto na instância self-hosted. **Vazia = desligado** — é o kill
   * switch: sem chave o chunk do tracker nem é baixado. Um deploy com o campo
   * vazio desliga a gravação inteira sem remover código.
   */
  projectKey: string;
  /** Endpoint de ingestão da nossa instância. */
  ingestPoint: string;
  /**
   * % de DISPOSITIVOS que gravam (0-100). Alavanca mestre de custo E de
   * performance: quem está fora nem baixa o chunk. Determinística por device
   * (nunca por evento) — a mesma pessoa está sempre dentro ou sempre fora,
   * senão a sessão sai picotada e o replay não conta a história inteira.
   *
   * Aqui o limitante não é quota de plano (a instância é nossa) e sim
   * ARMAZENAMENTO. Ver a escada de rollout no OPENREPLAY.md.
   */
  clientSamplePct: number;
  /**
   * Lojas em que a gravação vale. **Lista vazia = todas as lojas.**
   *
   * É o lugar provisório da decisão "esta loja grava?" enquanto a flag
   * `modules.openreplay` não existe no backend. Quando existir, a mudança é
   * dentro de `lojaNoEscopo()` e em mais lugar nenhum.
   */
  restaurantAllowList: number[];
  /**
   * Trocar por `0` todo número que aparece em nó de TEXTO (não em input).
   *
   * Ligada, cobre telefone, CPF, número da casa e valor do troco mesmo numa
   * tela que ninguém marcou — é a rede de segurança do mascaramento dirigido,
   * que por definição falha aberto. Em troca, preço aparece como `R$ 00,00`.
   *
   * É alavanca de environment, e não constante, porque a troca é real: começa
   * ligada e só se desliga quando o `check:openreplay-pii` tiver provado
   * cobertura por um ciclo inteiro.
   */
  obscureTextNumbers: boolean;
  /**
   * Como o CSS chega à gravação (`InlineCssMode` do SDK).
   *
   *   0 Disabled      — default; o BACKEND busca e cacheia o arquivo linkado
   *   1 Inline        — grava o CSS linkado como objeto AdoptedStyleSheet
   *   2 InlineFetched — busca o arquivo e simula AdoptedStyleSheets no replay
   *   3 PlainFetched  — busca o arquivo e salva como <style> puro
   *
   * Existe como alavanca por causa de um sintoma concreto: o CSS dos
   * componentes do Ionic vem de `adoptedStyleSheets` na shadow root, e sem ele
   * o `ion-modal` nunca fica escondido no replay — a gravação parece "travada"
   * numa tela enquanto a URL avança por baixo. No SDK, esta chave é repassada
   * ao `ShadowRootObserver`, que é justamente o caminho suspeito.
   *
   * CUSTO: os modos 2 e 3 fazem o tracker BUSCAR os arquivos CSS em runtime —
   * o próprio SDK avisa que afeta performance. Por isso produção fica em 0 até
   * o experimento no yellow dizer que vale.
   */
  inlineCss: number;
  /**
   * Manter ligado o "maintainer" de nós do SDK.
   *
   * Ele roda a cada 30s e **desregistra nós que julga destacados**
   * (`MaintainerOptions { interval: 30000, batchSize: 2500, enabled: true }`).
   *
   * Este app navega com `ion-router-outlet`, que DESTACA e REANEXA páginas
   * inteiras em vez de recriá-las. A suspeita é que o maintainer desregistra
   * uma página destacada e, quando ela volta, as mutações referenciam um pai
   * que o gravador não conhece mais — o efeito medido em 28/08/2026 foi o DOM
   * do replay congelar (contagem de nós IDÊNTICA com 40s de diferença)
   * enquanto as mensagens de URL continuavam chegando.
   *
   * O próprio SDK avisa que os defaults são "tested and proven to be
   * lightweight and easy on cpu" — desligar troca CPU/memória por fidelidade,
   * e por isso só o yellow desliga.
   */
  nodesMaintainer: boolean;
  /**
   * Instalar o nosso `domSanitizer` (`nivelDoElemento`).
   *
   * Existe para um A/B de UMA variável: separar o nosso código do SDK na
   * investigação do DOM que congela (28/08/2026). Desligado, a privacidade NÃO
   * regride — `defaultInputMode: Hidden` continua barrando tudo que é digitado,
   * e as marcações `data-openreplay-*` nos templates continuam valendo. O que
   * se perde são as regras estruturais (mapas, `credit-card`), e por isso isto
   * só pode ficar desligado num ambiente de validação, nunca em produção.
   */
  domSanitizerAtivo: boolean;
  /**
   * Abrir uma sessão NOVA a cada carga de página, em vez de retomar a anterior.
   *
   * Por padrão o tracker guarda o token em `sessionStorage` e RETOMA: recarregar
   * (e até abrir outra aba do mesmo perfil) mantém o mesmo `sessionId` e só
   * incrementa `pageno`. Medido em 28/08/2026.
   *
   * O problema é que, na retomada, **tudo a partir da segunda carga some da
   * gravação** — nem o DOM nem as mudanças de URL entram no arquivo. O replay
   * congela na última tela da primeira página enquanto o usuário segue usando o
   * app. Reproduzido: mesma navegação com e sem reload, `APP-SHOPPING-CART`
   * presente sem reload e ausente com. Ver OPENREPLAY.md §2.6.
   *
   * CONTRAPARTIDA de ligar: quem recarrega no meio do pedido vira duas sessões,
   * e a jornada fica picotada no painel. É pior para análise de funil e muito
   * melhor que gravação congelada — mas é troca, não almoço grátis.
   */
  forcarNovaSessao: boolean;
  /**
   * Furar o gate de localhost — SÓ para o ambiente de desenvolvimento local
   * (`environment.local.ts`), que roda a app em `localhost` apontada para a API
   * real para iterar no código do replay sem deploy.
   *
   * Ausente/`false` em TODO environment que vai a deploy: produção, green, blue,
   * yellow e o `environment.ts` de dev seguem barrando localhost. É por isso que
   * a exceção mora numa flag e não numa mudança em `ehLocalhost` — um ambiente
   * que nunca sai da máquina do dev é o único lugar em que ela existe.
   */
  permitirLocalhost?: boolean;
}

/** O que o app sabe sobre o ambiente na hora de decidir. */
export interface OpenReplayGateContext {
  production: boolean;
  hostname: string;
  isCypress: boolean;
  doNotTrack: boolean;
  deviceId: string;
  /**
   * Loja resolvida. `null` no agendamento (ela ainda não existe) e no caso em
   * que o cardápio falhou — loja inexistente ou o prazo de 15s do
   * `APP_INITIALIZER` estourado (`restaurantService.falhaDeCarga === 'prazo'`).
   */
  restaurantId: number | null;
  /**
   * `modules` do `cardapio.json`. Hoje ninguém lê — está no contexto para que a
   * flag `modules.openreplay`, quando existir no backend, entre em
   * `lojaNoEscopo()` sem mudar a assinatura de nada.
   */
  restaurantModules: { [k: string]: any } | null;
}

/**
 * Motivo da decisão. Sai como string (e não boolean) de propósito: quando
 * alguém perguntar "por que não estou vendo sessão nenhuma?", a resposta é uma
 * consulta a `window.__mpOpenReplay`, não uma investigação.
 */
export type MotivoBloqueio =
  | 'sem-project-key'
  | 'nao-producao'
  | 'localhost'
  | 'cypress'
  | 'do-not-track'
  | 'fora-da-amostra'
  | 'loja-fora-do-escopo'
  | '';

export interface DecisaoCarga {
  carregar: boolean;
  motivo: MotivoBloqueio;
}

/**
 * Esta loja está no escopo da gravação?
 *
 * **É AQUI que a flag por loja entra quando o backend a tiver.** Hoje a regra é
 * a allowlist do environment; amanhã será
 * `!!ctx.restaurantModules?.openreplay` (e, se houver rollout percentual, um
 * `inSample(ctx.deviceId, modules.openreplay_rollout_percent)` por cima). Nada
 * fora desta função precisa mudar — nem o serviço, nem o runtime, nem os specs
 * dos outros gates.
 *
 * Loja ainda não resolvida (`null`) NÃO reprova: quem chama no agendamento não
 * tem como saber, e reprovar ali desligaria o tracker para todo mundo. O gate
 * de verdade acontece no `decidirCarga`, com a loja na mão.
 */
export function lojaNoEscopo(cfg: OpenReplayConfig, ctx: OpenReplayGateContext): boolean {
  if (!cfg.restaurantAllowList || cfg.restaurantAllowList.length === 0) return true;
  if (ctx.restaurantId === null || ctx.restaurantId === undefined) return true;
  return cfg.restaurantAllowList.indexOf(ctx.restaurantId) !== -1;
}

/**
 * Gates que não dependem da loja — os únicos que dá para avaliar no bootstrap.
 *
 * Ordem do mais barato/definitivo para o mais caro, e o primeiro que reprovar
 * decide. `cypress` vem antes de tudo que depende de ambiente porque o E2E roda
 * com `production === false` em localhost e mesmo assim precisa de um motivo
 * próprio: sob Cypress o gate NÃO é furado (ao contrário da telemetria própria,
 * cujo endpoint é interceptável — aqui o destino é a instância de verdade, e um
 * E2E não pode encher o disco com sessão sintética).
 */
export function decidirAgendamento(
  cfg: OpenReplayConfig | undefined,
  ctx: OpenReplayGateContext,
): DecisaoCarga {
  if (!cfg || !cfg.projectKey) return { carregar: false, motivo: 'sem-project-key' };
  if (ctx.isCypress) return { carregar: false, motivo: 'cypress' };
  if (!ctx.production) return { carregar: false, motivo: 'nao-producao' };
  // `permitirLocalhost` só é `true` no environment.local — ver o campo no config.
  if (ehLocalhost(ctx.hostname) && !cfg.permitirLocalhost) return { carregar: false, motivo: 'localhost' };
  // DNT: isto grava a TELA da pessoa. Respeitar o sinal é o mínimo defensável
  // quando a captura vai muito além de contar erro.
  if (ctx.doNotTrack) return { carregar: false, motivo: 'do-not-track' };
  // Device SEM id fica fora, e o gate é explícito: `inSample('')` devolve bucket
  // 0, ou seja, um device sem id entraria em QUALQUER coorte ≥1% — o oposto do
  // pretendido, e concentrado justamente nos ambientes onde o storage falha
  // (modo privado, iframe). Sem id estável não há coorte estável.
  if (!ctx.deviceId) return { carregar: false, motivo: 'fora-da-amostra' };
  if (!inSample(ctx.deviceId, cfg.clientSamplePct)) return { carregar: false, motivo: 'fora-da-amostra' };
  return { carregar: true, motivo: '' };
}

/**
 * Todos os gates, reavaliados no `load`. É esta a decisão que autoriza o
 * `import()` do SDK.
 *
 * Reavaliar os gates baratos de novo não é redundância inútil: entre o
 * bootstrap e o `load` a pessoa pode ter navegado para outra loja, e o custo de
 * repetir sete comparações é zero perto do de gravar quem não devia.
 */
export function decidirCarga(
  cfg: OpenReplayConfig | undefined,
  ctx: OpenReplayGateContext,
): DecisaoCarga {
  const base = decidirAgendamento(cfg, ctx);
  if (!base.carregar) return base;
  if (!lojaNoEscopo(cfg as OpenReplayConfig, ctx)) {
    return { carregar: false, motivo: 'loja-fora-do-escopo' };
  }
  return { carregar: true, motivo: '' };
}
